import { useCallback } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection, OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { PayrollCycleFragment, PayrollCyclesDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { CyclesLocationCallout } from 'modules/bookkeepers/components/CyclesLocationCallout'
import useGetCycleCount from 'modules/bookkeepers/hooks/useGetCycleCount'
import useGetPayrollResults from 'modules/bookkeepers/hooks/useGetPayrollResults'
import { useIncidenceFilterContext } from 'modules/bookkeepers/lib/IncidenceFilterContext'
import {
  PayrollRunStatus,
  getPayrollRunStatusText,
  getPayrollRunStatusVariant,
} from 'modules/compensations/lib/payrollRunStatus'

function PayrollCyclesPage() {
  const { navigation } = useNavigation()
  const { selectedCompany, filterDate, setSelectedCompany, setFilterDate } =
    useIncidenceFilterContext()
  const { cyclesCount } = useGetCycleCount()
  const { payrollResults } = useGetPayrollResults()
  const payrollResultsCount = payrollResults.length

  const showCallout = cyclesCount > 0 && payrollResultsCount > 0

  const bookkeeperContext = {
    isBookkeeperView: true,
  }

  const { loading: payrollCyclesLoading, data: payrollCyclesData } = useQuery(
    PayrollCyclesDocument,
    {
      variables: {
        companyIds: [selectedCompany?.attributes.id],
        dateFrom: filterDate.firstDate,
        dateTo: filterDate.lastDate,
      },
      context: bookkeeperContext,
    }
  )
  const payrollCycles = payrollCyclesData?.compensations

  const cyclesStatusesCount = useCallback(
    (status: string) => {
      return () => {
        if (!payrollCycles) return 0

        switch (status) {
          case PayrollRunStatus.CALCULATION:
            return payrollCycles.calculation.totalCount ?? 0
          case PayrollRunStatus.IN_REVIEW:
            return payrollCycles.inReview.totalCount ?? 0
          case PayrollRunStatus.IN_PREPARATION:
            return payrollCycles.inPreparation.totalCount ?? 0
          case PayrollRunStatus.COMPLETED:
            return payrollCycles.completed.totalCount ?? 0
          default:
            return 0
        }
      }
    },
    [payrollCycles]
  )

  const source = useGraphqlDataCollectionSource(
    {
      document: PayrollCyclesDocument,
      queryContext: bookkeeperContext,
      mapData: (data) => {
        return data.compensations.payrollRunsConnection.nodes
          .map((item) => {
            return {
              ...item,
            }
          })
          .sort((a, b) => {
            const dateA = a.endsOn || a.startsOn || ''
            const dateB = b.endsOn || b.startsOn || ''
            return dateB.localeCompare(dateA)
          })
      },
      mapPagination: (data) => {
        return {
          total: data.compensations.payrollRunsConnection.totalCount ?? 0,
        }
      },
      perPage: 10,
      mapVariables: ({ filters }) => {
        const status = filters?.status && filters?.status.length > 0 ? filters.status : undefined

        return {
          ...(selectedCompany && { companyIds: [selectedCompany.attributes.id] }),
          ...(filterDate.firstDate && { dateFrom: filterDate.firstDate }),
          ...(filterDate.lastDate && { dateTo: filterDate.lastDate }),
          ...(status && { statuses: status }),
        }
      },
      itemUrl: (item) =>
        navigation.bookkeepers.clients.payrollCycles.details.fullPath({
          id: item.id,
        }),
      currentFilters: { status: ['calculation'] },
      filters: {
        status: {
          label: i18n.t('bookkeepers.clients.payroll_cycles.list_columns.status'),
          type: 'in',
          options: {
            options: [
              {
                label: i18n.t('bookkeepers.clients.payroll_cycles.statuses.in_review'),
                value: 'in_review',
              },
              {
                label: i18n.t('bookkeepers.clients.payroll_cycles.statuses.in_preparation'),
                value: 'in_preparation',
              },
              {
                label: i18n.t('bookkeepers.clients.payroll_cycles.statuses.calculation'),
                value: 'calculation',
              },
              {
                label: i18n.t('bookkeepers.clients.payroll_cycles.list_columns.status'),
                value: 'completed',
              },
            ],
          },
        },
      },
      presets: [
        {
          label: i18n.t('bookkeepers.clients.payroll_cycles.statuses.in_preparation'),
          filter: { status: ['in_preparation'] },
          itemsCount: payrollCyclesLoading ? undefined : cyclesStatusesCount('in_preparation'),
        },

        {
          label: i18n.t('bookkeepers.clients.payroll_cycles.statuses.calculation'),
          filter: { status: ['calculation'] },
          itemsCount: payrollCyclesLoading ? undefined : cyclesStatusesCount('calculation'),
        },
        {
          label: i18n.t('bookkeepers.clients.payroll_cycles.statuses.in_review'),
          filter: { status: ['in_review'] },
          itemsCount: payrollCyclesLoading ? undefined : cyclesStatusesCount('in_review'),
        },
        {
          label: i18n.t('bookkeepers.clients.payroll_cycles.statuses.completed'),
          filter: { status: ['completed'] },
          itemsCount: payrollCyclesLoading ? undefined : cyclesStatusesCount('completed'),
        },
      ],
    },
    [selectedCompany?.attributes.id, filterDate.firstDate, filterDate.lastDate]
  )

  const hasSidebarFilters = () => {
    const hasDateFilters = !!filterDate.firstDate && !!filterDate.lastDate
    const hasCompanyFilter = !!selectedCompany?.attributes.id
    return hasDateFilters || hasCompanyFilter
  }

  return (
    <Box
      width="full"
      style={{
        maxWidth: '1440',
      }}
      padding="s12"
    >
      <ResourceHeader title={i18n.t('bookkeepers.clients.payroll_cycles.title')} />
      {showCallout && (
        <Box padding="s12">
          <CyclesLocationCallout />
        </Box>
      )}
      <OneDataCollection
        emptyStates={{
          'no-results': {
            emoji: '🧐',
            title: i18n.t('bookkeepers.clients.payroll_cycles.empty_state.no_results_title'),
            description: i18n.t(
              'bookkeepers.clients.payroll_cycles.empty_state.no_results_description'
            ),
            actions: hasSidebarFilters()
              ? [
                  {
                    label: i18n.t('bookkeepers.clients.payroll_cycles.empty_state.cta'),
                    onClick: () => {
                      setSelectedCompany(null)
                      setFilterDate({})
                    },
                  },
                ]
              : undefined,
          },
          'no-data': {
            emoji: '🧐',
            title: i18n.t('bookkeepers.clients.payroll_cycles.empty_state.no_results_title'),
            description: i18n.t(
              'bookkeepers.clients.payroll_cycles.empty_state.no_results_description'
            ),
            actions: hasSidebarFilters()
              ? [
                  {
                    label: i18n.t('bookkeepers.clients.payroll_cycles.empty_state.cta'),
                    onClick: () => {
                      setSelectedCompany(null)
                      setFilterDate({})
                    },
                  },
                ]
              : undefined,
          },
        }}
        source={source}
        visualizations={[
          {
            type: 'list',
            options: {
              itemDefinition: (item: PayrollCycleFragment) => ({
                title: item.cycle?.company?.name || '',
              }),
              fields: [
                {
                  label: i18n.t('bookkeepers.clients.payroll_cycles.list_columns.period'),
                  render: (item: PayrollCycleFragment) => item.label,
                },
                {
                  label: i18n.t('bookkeepers.clients.payroll_cycles.list_columns.status'),
                  render: ({ status }) => ({
                    type: 'status',
                    value: {
                      status: getPayrollRunStatusVariant(status),
                      label: getPayrollRunStatusText(status),
                    },
                  }),
                },
              ],
            },
          },
        ]}
      />
    </Box>
  )
}

export default PayrollCyclesPage
